import React, { useState, useEffect } from "react";
import { MentorAvatar } from "./Logo";
import { Sparkles, ArrowRight, Check, Terminal, FolderGit, Layout, Cpu } from "lucide-react";

export default function ExperienceGapPage({ onOpenModal }) {
  const [terminalLogs, setTerminalLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("microservices");
  
  // Terminal commands typewriter simulation
  useEffect(() => {
    const logs = [
      "⚡ Booting Docker environment on microservices/auth...",
      "🐳 container 'vph-auth-db' healthy [PostgreSQL]",
      "📦 resolving npm/yarn production dependencies...",
      "🐳 spawning Redis container for memory caching...",
      "🚀 Microservice listening on port 8000 (gRPC enabled)",
      "🌱 running production seed migration scripts...",
      "📈 connecting pipeline telemetry dashboard...",
      "✅ AUTH MICROSERVICE STANDING AT 100% HEALTH!"
    ];

    setTerminalLogs([logs[0]]);
    let currentIdx = 1;
    const interval = setInterval(() => {
      if (currentIdx < logs.length) {
        setTerminalLogs((prev) => [...prev, logs[currentIdx]]);
        currentIdx++;
      } else {
        clearInterval(interval);
      }
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  const archBlueprints = {
    microservices: {
      title: "Production Microservices Cluster",
      tech: "Docker, Kubernetes, Redis, gRPC, PostgreSQL",
      desc: "An enterprise-grade decoupled backend processing peak loads. Teaches you how to partition databases and manage state across containers.",
      svg: (
        <svg viewBox="0 0 400 220" className="w-full h-auto bg-slate-950 p-4 border border-zinc-800 rounded-2xl">
          <rect x="20" y="20" width="100" height="50" rx="8" fill="#064E3B" stroke="#10B981" strokeWidth="1.5" />
          <text x="70" y="49" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Web Gateway</text>
          
          <rect x="150" y="20" width="100" height="50" rx="8" fill="#18181B" stroke="#27272A" strokeWidth="1.5" />
          <text x="200" y="49" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Auth Service</text>

          <rect x="280" y="20" width="100" height="50" rx="8" fill="#18181B" stroke="#27272A" strokeWidth="1.5" />
          <text x="330" y="49" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Billing Core</text>

          {/* Connectors */}
          <path d="M120 45H150" stroke="#10B981" strokeWidth="2" strokeDasharray="3 3" />
          <path d="M250 45H280" stroke="#10B981" strokeWidth="2" strokeDasharray="3 3" />

          {/* Redis Cache Box */}
          <rect x="150" y="120" width="100" height="50" rx="8" fill="#1e1b4b" stroke="#818cf8" strokeWidth="1.5" />
          <text x="200" y="149" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Redis Cache Tier</text>
          <path d="M200 70V120" stroke="#10B981" strokeWidth="1.5" />
        </svg>
      )
    },
    pipelines: {
      title: "Enterprise CI/CD Deployment Flow",
      tech: "GitHub Actions, AWS S3, Docker Registry, Terraform",
      desc: "Robust deployment architecture ensuring 100% test validation and zero-downtime rolling updates. Mimics a high-scale tech company process.",
      svg: (
        <svg viewBox="0 0 400 220" className="w-full h-auto bg-slate-950 p-4 border border-zinc-800 rounded-2xl">
          <circle cx="60" cy="110" r="30" fill="#064E3B" stroke="#10B981" strokeWidth="2" />
          <text x="60" y="114" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Git Commit</text>

          <path d="M90 110H140" stroke="#10B981" strokeWidth="2" />

          <rect x="140" y="80" width="110" height="60" rx="8" fill="#18181B" stroke="#27272A" strokeWidth="2" />
          <text x="195" y="115" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">GitHub Actions</text>

          <path d="M250 110H300" stroke="#10B981" strokeWidth="2" />

          <circle cx="340" cy="110" r="30" fill="#1e1b4b" stroke="#818cf8" strokeWidth="2" />
          <text x="340" y="114" fill="#FAFAFA" fontSize="11" fontWeight="bold" textAnchor="middle">Live AWS</text>
        </svg>
      )
    }
  };

  return (
    <div className="bg-[#09090B] text-white font-sans pt-20">
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#064E3B]/10 to-[#09090B] py-16 md:py-24 border-b border-zinc-800">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,#059669,transparent_30%)] opacity-20" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
              <Sparkles className="h-3 w-3" /> The Proof-of-Work Equalizer
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]" data-testid="hero-heading">
              Degree is <span className="text-[#10B981] underline decoration-wavy">just a paper?</span>
            </h1>
            
            <h2 className="text-lg sm:text-xl font-extrabold text-zinc-300 tracking-tight leading-relaxed">
              Unlock Your Career Clarity: From lack of campus connections to proving your capability with live projects.
            </h2>
            
            <p className="text-[#A1A1AA] text-sm sm:text-base max-w-xl leading-relaxed">
              Hiring managers prefer candidates with branded college tags or internships. But when you build real production systems that solve business problems, your proof of work speaks louder than any college degree.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Experience Gap Strategy")}
                className="px-6 py-4 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-[#10B981]/20 transition-all"
                data-testid="book-free-clarity-call-btn"
              >
                Book Free Clarity Call <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Experience Gap Strategy")}
                className="px-6 py-4 bg-zinc-900 hover:bg-zinc-800 text-white font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 transition-all border border-zinc-800"
                data-testid="register-for-free-webinar-btn"
              >
                Register for Free Webinar
              </button>
            </div>
          </div>

          {/* Right Column: Code terminal live typing widget */}
          <div className="lg:col-span-5 bg-zinc-950 border border-zinc-800/80 rounded-3xl p-5 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                <Terminal className="h-3 w-3 text-[#10B981]" /> dev-cluster-vph::active
              </span>
            </div>

            <div className="font-mono text-xs text-zinc-400 space-y-2.5 min-h-[220px] max-h-[240px] overflow-y-auto scrollbar-thin">
              {terminalLogs.map((log, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <span className="text-zinc-600 select-none">&gt;</span>
                  <span className={log.includes("health") || log.includes("HEALTH") ? "text-[#10B981]" : "text-zinc-300"}>{log}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center justify-between text-[11px] text-zinc-500 font-mono">
              <span>Branch: production-stage</span>
              <span className="text-[#10B981] animate-pulse">● server live</span>
            </div>
          </div>

        </div>
      </section>

      {/* 2. Pain Identification */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-black uppercase tracking-wider text-[#10B981]">The Placement Deficit</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white mt-2 mb-4" data-testid="pain-heading">
            Is this your struggle?
          </h2>
          <div className="space-y-4 text-lg text-zinc-400 font-medium">
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" /> Complete absence of campus recruitment drives in your region.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" /> No industry connections or reference networks to get you referred.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]" /> College projects look generic and fail to stand out compared to elite grads.
            </p>
          </div>
          <div className="mt-6 border-l-4 border-[#10B981] pl-4 py-2 italic font-black text-white text-lg md:text-xl leading-relaxed">
            "It’s not just rejection. It’s the frustration of having zero support from your college system."
          </div>
        </div>
      </section>

      {/* 3. Behind the Pain (Storytelling Case Study) */}
      <section className="bg-zinc-900/50 py-16 border-t border-b border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4 flex justify-center">
            <MentorAvatar />
          </div>
          <div className="md:col-span-8 space-y-4">
            <span className="text-xs font-black uppercase tracking-wider text-[#10B981]">Graduate Success Story</span>
            <h3 className="text-2xl font-extrabold text-white tracking-tight">
              Ananya graduated from a remote Tier-3 college with no local placement opportunities.
            </h3>
            <p className="text-[#A1A1AA] leading-relaxed text-sm sm:text-base">
              "Every online opening asked for past internships or references. I had neither. Mr. Vedula helped me build actual production-level systems using our <strong>Gap Fillers</strong> blueprints. I deployed a complete microservice architecture to the web. When interviewers saw my live, working system, they skipped asking about my college background entirely."
            </p>
            <div className="flex items-center gap-4 pt-2">
              <div className="text-xs font-bold uppercase tracking-wider bg-[#10B981]/10 text-[#10B981] px-3 py-1 rounded-full border border-[#10B981]/20">
                Full-Stack Software Hire
              </div>
              <div className="text-xs font-bold uppercase tracking-wider bg-amber-500/10 text-amber-500 px-3 py-1 rounded-full border border-amber-500/20">
                Bypassed College Disadvantage
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Production Architecture Blueprint Viewer */}
      <section className="py-16 bg-black border-b border-zinc-800/60">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <span className="text-xs font-black uppercase tracking-wider text-[#10B981]">Interactive Architecture Viewer</span>
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Your Proof-of-Work Canvas</h3>
          <p className="text-zinc-400 text-sm max-w-xl mx-auto">
            Interact with the buttons below to switch blueprints and see the senior engineering systems Mr. Vedula has you build.
          </p>

          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={() => setActiveTab("microservices")}
              className={`flex items-center gap-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all ${
                activeTab === "microservices" ? "bg-[#10B981] text-[#09090B]" : "bg-zinc-900 text-zinc-400 border border-zinc-800"
              }`}
            >
              <Cpu className="h-4 w-4" /> Microservices Cluster
            </button>
            <button
              onClick={() => setActiveTab("pipelines")}
              className={`flex items-center gap-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase transition-all ${
                activeTab === "pipelines" ? "bg-[#10B981] text-[#09090B]" : "bg-zinc-900 text-zinc-400 border border-zinc-800"
              }`}
            >
              <FolderGit className="h-4 w-4" /> Deployment Pipeline
            </button>
          </div>

          <div className="mt-6 space-y-4">
            <div className="p-4 bg-zinc-900/30 border border-zinc-800/80 rounded-3xl">
              {archBlueprints[activeTab].svg}
            </div>
            <div className="text-left bg-zinc-950 p-5 border border-zinc-800/60 rounded-2xl">
              <h4 className="font-extrabold text-white text-base flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#10B981]" /> {archBlueprints[activeTab].title}
              </h4>
              <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mt-1">Tech Stack: {archBlueprints[activeTab].tech}</p>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{archBlueprints[activeTab].desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Solution Framework */}
      <section className="py-16 md:py-24 bg-zinc-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-black uppercase tracking-wider text-[#10B981]">The Framework</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="solution-heading">
              Your Career GPS System
            </h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              A comprehensive program specifically compiled to address and solve experience blocks for developers.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 font-sans">
            <div className="bg-zinc-900/40 border border-zinc-800/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center font-black">01</div>
              <h3 className="font-extrabold text-lg text-white">Resume Rescue</h3>
              <p className="text-zinc-400 text-sm">
                Translate standard engineering tasks into high-impact, business-driven, ATS-destroyer achievements.
              </p>
            </div>
            
            <div className="bg-zinc-900/40 border border-zinc-800/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center font-black">02</div>
              <h3 className="font-extrabold text-lg text-white">Interview Anchor</h3>
              <p className="text-zinc-400 text-sm">
                Confidently script STAR scenarios and system design reviews, eliminating nervousness.
              </p>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center font-black">03</div>
              <h3 className="font-extrabold text-lg text-white">Gap Fillers</h3>
              <p className="text-zinc-400 text-sm">
                Incorporate robust docker models, scale multi-tenant architectures, and produce real proof of development.
              </p>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800/80 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center font-black">04</div>
              <h3 className="font-extrabold text-lg text-white">Dream 50 Tracker</h3>
              <p className="text-zinc-400 text-sm">
                Identify, track, and bypass gates at the top 50 organizations seeking senior dev assets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Silver Membership Pitch */}
      <section className="py-16 md:py-24 bg-[#09090B] border-t border-zinc-800/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#064E3B] text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-8 space-y-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-400 text-[#09090B]">
                👑 Platinum Standard Curriculum
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="silver-heading">
                Upgrade to Silver Membership
              </h2>
              <div className="space-y-3 text-emerald-100 text-sm">
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Gap Fillers Workbook</strong> (Production docker/kubernetes microservices code)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Resume Rescue Blueprint</strong> (Elite layout templates)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Interview Anchor Toolkit</strong> (STAR framework design sheets)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>90-Day Production Playbook</strong> (Lifetime roadmap framework)
                </p>
              </div>
              <div className="pt-2">
                <span className="text-xs text-amber-300 font-bold block mb-1">🎁 EXCLUSIVE BONUSES INCLUDED:</span>
                <span className="text-xs text-emerald-100 font-medium">3 Free 1-on-1 Clarity Calls + Lifetime Weekly Q&A Webinar Masterminds</span>
              </div>
            </div>
            
            <div className="md:col-span-4 bg-black/25 border border-white/10 p-6 rounded-2xl text-center space-y-4">
              <div className="text-emerald-200 text-xs font-black uppercase">One-Time Fee</div>
              <div className="text-4xl font-black text-amber-400">₹4,999</div>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a187ad45eaf79ab7261b65e", "Join Silver Membership Now", "Experience Strategy Silver Membership")}
                className="w-full py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-sm uppercase rounded-xl shadow-lg transition-all"
                data-testid="join-silver-membership-btn"
              >
                Join Silver Now
              </button>
              <span className="text-[10px] text-emerald-200 block">Immediate Digital Checkout</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Options Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 font-sans">
        <div className="text-center space-y-2">
          <span className="text-xs font-black uppercase tracking-wider text-[#10B981]">Secure Your Slot</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Engineering Acceleration Pathways</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-zinc-800 bg-zinc-900/20 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#10B981] uppercase tracking-widest block">1-on-1 DIAGNOSTIC</span>
              <h3 className="font-extrabold text-xl text-white">Free Clarity Call</h3>
              <p className="text-zinc-400 text-sm">
                Map out an elite architectural growth path customized for your current tech stack.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Experience Strategy Free Call")}
              className="w-full mt-6 py-3.5 bg-[#10B981] hover:bg-[#34D399] text-[#09090B] font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-clarity-call-btn"
            >
              Book Call (3 slots left)
            </button>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/20 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#10B981] uppercase tracking-widest block">DEPLOYMENT WORKSHOP</span>
              <h3 className="font-extrabold text-xl text-white">Exclusive Live Webinar</h3>
              <p className="text-zinc-400 text-sm">
                See Mr. Vedula deploy a live microservices API cluster in real-time. Bypasses gatekeeper audits.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Experience Strategy Live Webinar")}
              className="w-full mt-6 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-webinar-btn"
            >
              Register for Webinar
            </button>
          </div>

          <div className="border border-amber-500/30 bg-amber-500/5 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-amber-500 uppercase tracking-widest block">COMPLETE TOOLKIT</span>
              <h3 className="font-extrabold text-xl text-slate-100">Silver Membership</h3>
              <p className="text-zinc-300 text-sm">
                Production architecture checklists, complete source codes, and lifetime mastermind webinar access.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://vedulaprogress.lovable.app", "Join Silver Membership Now", "Experience Strategy Silver Membership")}
              className="w-full mt-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-silver-btn"
            >
              Join Silver Membership
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
